import type { AskUserQuestion } from 'features/ask-yogi/types'

export type AskUserQuestionAnswers = Record<string, string | string[]>

export type QuestionState = {
  // Labels the user selected from the provided options.
  selectedLabels: string[]
  // Whether the injected "Other" free-text option is active.
  otherActive: boolean
  otherText: string
}

export const emptyQuestionState = (): QuestionState => ({
  selectedLabels: [],
  otherActive: false,
  otherText: '',
})

// The model tags an option label with a trailing "(Recommended)" as a UI hint.
// It is not part of the answer, so strip it from a chosen option's value. The
// user's free-text "Other" answer is never passed through this.
const stripRecommended = (label: string): string =>
  label.replace(/\s*\(Recommended\)\s*$/, '').trim()

export const collectValues = (state: QuestionState): string[] => {
  const values = state.selectedLabels.map(stripRecommended)
  const trimmed = state.otherText.trim()
  if (state.otherActive && trimmed.length > 0) values.push(trimmed)
  return values
}

/**
 * Reduce the per-question UI state into a structured map keyed by each
 * question's `header`. Single-select questions map to the chosen label (a
 * string); multi-select questions map to an array of labels. The user's
 * free-text "Other" answer is folded in as a value. Unanswered questions are
 * omitted. Kept as the tested internal representation; the string actually
 * sent to the backend is produced by `formatAskUserQuestionAnswers`.
 */
export const buildAskUserQuestionAnswers = (
  questions: AskUserQuestion[],
  states: QuestionState[],
): AskUserQuestionAnswers => {
  const answers: AskUserQuestionAnswers = {}
  questions.forEach((question, index) => {
    const state = states[index] ?? emptyQuestionState()
    const values = collectValues(state)
    if (values.length === 0) return
    answers[question.header] = question.multi_select ? values : values[0]!
  })
  return answers
}

/**
 * Render the answered questions as a human-readable message — one line per
 * answered question, `"<header>: <value>"`, with multi-select values
 * comma-joined. This is what gets sent to the backend (and shown in the user's
 * chat bubble) so the turn reads cleanly instead of as raw JSON. Unanswered
 * questions are omitted, matching the structured builder.
 */
export const formatAskUserQuestionAnswers = (
  questions: AskUserQuestion[],
  states: QuestionState[],
): string =>
  questions
    .map((question, index) => {
      const values = collectValues(states[index] ?? emptyQuestionState())
      if (values.length === 0) return null
      return `${question.header}: ${values.join(', ')}`
    })
    .filter((line): line is string => line !== null)
    .join('\n')
